import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList"

export const indexName: string = process.env.ALGOLIA_SEARCH_INDEX_NAME ?? ''

export const hierarchicalDelimiter = ' > '
export const searchInputDebounceMs = 300
export const searchMinimumQueryLength = 3

// thank you Cory and sharp.com ~
export const transformItems = (items: RefinementListItem[]): RefinementListItem[] => {
  // the resulting tree
  const rootItems = []

  // cache of previously processed items
  const lookup = new Map()

  // delimiter for algolia hierarchy
  const delim = hierarchicalDelimiter

  // loop over items
  for (const item of items) {
    const levels = item.value.split(delim)
    const parentId = levels[levels.length - 2]
    const itemId = item.value

    // fix label to only show last level
    item.label = levels[levels.length - 1]

    // check lookup
    if (!lookup.has(itemId)) {
      // item not found, add placeholder data for now
      lookup.set(itemId, { data: [] })
    }

    // update lookup with current item data
    lookup.set(itemId, { ...item, data: lookup.get(itemId).data })

    const treeItemPointer = lookup.get(itemId)

    // if root item
    if (typeof parentId === 'undefined') {
      rootItems.push(treeItemPointer)
    }
    // else nested item
    else {
      // check lookup for parent
      if (!lookup.has(parentId)) {
        // parent not found, add placeholder data for now
        lookup.set(parentId, { data: [] })
      }

      // add the current item to the parent
      lookup.get(parentId).data.push(treeItemPointer)
    }
  }

  return rootItems
}

// Provider / physician helper utilities
/**
 * Return a human-friendly display name for a provider hit.
 *
 * Prefers already-formatted name fields (which may include degrees),
 * and falls back to a constructed `firstname lastname` string. This keeps
 * UI rendering consistent across different index schemas and content sources.
 */
export function getProviderDisplayName(hit: any): string {
  return (
    hit.name_formatted_with_degrees || hit.name_formatted || `${hit.firstname ?? ''} ${hit.lastname ?? ''}`.trim()
  )
}

/**
 * Choose the primary place (practice/location) for a provider hit.
 *
 * Selection strategy:
 * - If individual `place.distance` values exist, pick the smallest (closest).
 * - Else if the hit contains an overall distance (Algolia geo info), assume
 *   `places` are already proximity-sorted and return the first.
 * - Else prefer a visible (not hidden) place, falling back to the first place.
 *
 * Reason: provider records can include multiple practice locations; the UI
 * needs a single canonical place to display addresses and distances.
 */
export function getPrimaryPlace(hit: any): any | null {
  const places = hit.places || []
  if (!places.length) return null

  // if places have per-place distance, choose the closest
  const placesWithDistance = places.filter((p: any) => typeof p.distance === 'number')
  if (placesWithDistance.length) {
    return placesWithDistance.reduce((min: any, p: any) => (p.distance < min.distance ? p : min), placesWithDistance[0])
  }

  // if overall hit distance present, assume Algolia returned places sorted by proximity
  if (hit.distance != null || hit._rankingInfo?.geoDistance != null || hit.geo_distance != null) {
    return places[0]
  }

  // prefer visible places (not flagged hidden)
  const visible = places.find((p: any) => !(p.tags || []).some((t: any) => t?.name === 'Hide on provider profile'))
  return visible ?? places[0]
}

/**
 * Format a place's address into a single display string.
 *
 * Concatenates street, secondary address, and a city/state/postal line while
 * gracefully handling missing parts. Returns `null` for falsy input.
 *
 * Reason: centralizes address formatting so cards and lists show consistent
 *, readable addresses across the app.
 */
export function formatPlaceAddress(place: any): string | null {
  if (!place) return null
  const parts: string[] = []
  if (place.street_address) parts.push(place.street_address)
  if (place.secondary_address) parts.push(place.secondary_address)
  const cityLine = [place.city, place.state, place.postal_code].filter(Boolean).join(', ')
  if (cityLine) parts.push(cityLine)
  return parts.join(', ')
}

/**
 * Normalize and format a distance value for UI.
 *
 * Accepts various possible fields (`distance`, Algolia `_rankingInfo.geoDistance`,
 * or `geo_distance`), converts large values (assumed meters) to miles, and
 * formats with appropriate precision (one decimal for <10 miles, integer otherwise).
 * Returns `null` when no valid distance is present.
 *
 * Reason: different data sources and transformations may expose distances
 * in different units or fields; this helper normalizes them for display.
 */
export function formatDistance(hit: any): string | null {
  const raw = hit.distance ?? hit._rankingInfo?.geoDistance ?? hit.geo_distance
  if (raw == null) return null
  let miles = Number(raw)
  if (isNaN(miles)) return null
  // if a large number (likely meters) convert to miles
  if (miles > 1000) {
    miles = miles / 1609.344
  }
  const precision = miles < 10 ? 1 : 0
  return `${miles.toFixed(precision)} mi`
}

/**
 * Build the canonical provider profile URL for a hit.
 *
 * Checks common slug fields and falls back to an absolute `url` if present.
 * Returns `#` when no navigable URL is available.
 *
 * Reason: provider records may use different fields for routing depending on
 * indexing pipelines; centralizing URL logic avoids duplicated checks.
 */
export function getProviderProfileUrl(hit: any): string {
  if (hit.slug) return `/doctors/${hit.slug}`
  if (hit.provider_slug) return `/doctors/${hit.provider_slug}`
  if (hit.url) return hit.url
  return '#'
}

/**
 * Pick the best image source for a provider hit.
 *
 * Supports multiple common field names (`photo.url`, `photo`, `photo_url`,
 * `image`) to be resilient to differences between CMS and indexed shapes.
 * Returns `null` when no image is available.
 *
 * Reason: keeps image selection logic in one place so components can simply
 * render the returned src if present.
 */
export function getProviderImageSrc(hit: any): string | null {
  return hit.photo?.url || hit.photo || hit.photo_url || hit.image || null
}
